import {
	AfterContentInit,
	ChangeDetectorRef,
	ContentChildren,
	Directive,
	HostBinding,
	HostListener,
	OnDestroy,
	QueryList,
} from "@angular/core";
import { MatSelect } from "@angular/material/select";
import { Subscription } from "rxjs";

@Directive({
	selector: "mat-form-field",
})
export class MatSelectToggleOnClickDirective
	implements AfterContentInit, OnDestroy
{
	@ContentChildren(MatSelect, { descendants: true })
	private matSelects?: QueryList<MatSelect>;

	private matSelect?: MatSelect;

	@HostBinding("class.customan-mat-select-open")
	isOpen = false;

	private subs = new Subscription();
	private selectOpenedSub: Subscription | null = null;

	/**
	 * Após fechar no mousedown, o browser ainda dispara o `click` do mesmo gesto;
	 * ignoramos esse único click para não reabrir. Se o `click` não chegar ao
	 * mat-form-field (ex.: preventDefault no mousedown), o setTimeout(0) limpa o flag.
	 */
	private suppressOneClick = false;

	constructor(private cdr: ChangeDetectorRef) {}

	ngAfterContentInit(): void {
		this.attachToFirstSelect();
		if (this.matSelects) {
			this.subs.add(
				this.matSelects.changes.subscribe(() => this.attachToFirstSelect())
			);
		}
	}

	ngOnDestroy(): void {
		this.selectOpenedSub?.unsubscribe();
		this.subs.unsubscribe();
	}

	private attachToFirstSelect(): void {
		const next =
			this.matSelects && this.matSelects.length > 0
				? this.matSelects.first
				: undefined;
		if (!next || this.matSelect === next) return;

		this.matSelect = next;
		this.syncArrowFromPanel();
		this.selectOpenedSub?.unsubscribe();
		this.selectOpenedSub = this.matSelect.openedChange.subscribe(() => {
			this.syncArrowFromPanel();
		});
	}

	/** Seta sempre igual a `panelOpen` — evita triângulo “pra cima” com lista fechada. */
	private syncArrowFromPanel(): void {
		if (!this.matSelect) return;
		const open = this.matSelect.panelOpen;
		if (this.isOpen !== open) {
			this.isOpen = open;
			this.cdr.markForCheck();
		}
	}

	private scheduleArrowResync(): void {
		Promise.resolve().then(() => this.syncArrowFromPanel());
		requestAnimationFrame(() => this.syncArrowFromPanel());
	}

	@HostListener("mousedown", ["$event"])
	onMouseDown(event: MouseEvent): void {
		if (!this.matSelect) return;
		if (this.matSelect.disabled) return;

		// Próximo gesto com painel fechado: não deixar suppress preso (duplo clique).
		if (!this.matSelect.panelOpen) {
			this.suppressOneClick = false;
		}

		if (this.matSelect.panelOpen) {
			event.preventDefault();
			event.stopPropagation();
			this.suppressOneClick = true;
			this.matSelect.close();
			this.scheduleArrowResync();
			setTimeout(() => {
				if (this.suppressOneClick) {
					this.suppressOneClick = false;
				}
			}, 0);
		}
	}

	@HostListener("click", ["$event"])
	onClick(event: MouseEvent): void {
		if (!this.matSelect) return;
		if (this.matSelect.disabled) return;

		if (this.suppressOneClick) {
			this.suppressOneClick = false;
			event.preventDefault();
			event.stopPropagation();
			this.scheduleArrowResync();
			return;
		}

		if (!this.matSelect.panelOpen) {
			event.preventDefault();
			event.stopPropagation();
			this.matSelect.focus();
			this.matSelect.open();
			this.scheduleArrowResync();
		}
	}
}
